import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { 
  BOOKING_STEPS, 
  BookingStateManager,
  CustomerDataManager,
  BookingButtonManager,
  isServiceFeeEnabled,
  isWellnessExpressCheckoutEnabled
} from '@theme/booking-state';


/**
 * CartDrawerComponent - Manages the cart drawer dialog
 * 
 * Coordinates between:
 * - cart-booking-journey (Step 1: Application flow)
 * - cart-booking-location (Step 2: Location & DateTime flow)
 * - cart-items-component (Step 3: Cart summary)
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    this.initComponents();
    this.setupFinaliseBookingHandler();
    this.setupCheckoutHandler();
    this.setupCloseWarningHandlers();
    this.updateCustomerDataState();
    this.updateNoBookingRequiredState();
    // Initial checkout button visibility check
    this.#updateCheckoutButtonVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }
  

  /**
   * Initialize references to booking components
   */
  initComponents() {
    this.cartBookingJourney = document.querySelector('cart-booking-journey');
    this.cartBookingLocation = document.querySelector('cart-booking-location');
  }

  #handleCartAdd = () => {
    console.log('[DEBUG #handleCartAdd]', { autoOpen: this.hasAttribute('auto-open') });
    if (this.hasAttribute('auto-open')) {
      // Use open() instead of showDialog() to ensure restoreBookingState is called
      this.open();
    }
  };

  #handleCartUpdate = () => {
    // Update progress steps when cart items change (add/remove)
    // Use setTimeout to ensure DOM has updated after cart morph
    // Use 500ms to allow for full section rendering
    setTimeout(() => {
      // Clear booking data if cart is now empty
      BookingStateManager.clearIfCartEmpty();
      BookingButtonManager.updateState();
      this.updateProgressSteps();
      // Update button visibility based on customer data state
      this.updateCustomerDataState();
      this.updateNoBookingRequiredState();
      // Re-apply checkout button visibility after cart re-renders
      this.#updateCheckoutButtonVisibility();
      // Restore booking summary if on step 3 (cart DOM may have been replaced)
      this.#restoreBookingSummaryIfNeeded();
      // Check if service fee should be removed (no govt products but service fee exists)
      this.#checkAndRemoveOrphanedServiceFee();
    }, 500);
  };
  
  /**
   * Restore booking summary visibility if booking is confirmed (step 3)
   * This is needed after cart morph replaces the DOM
   */
  #restoreBookingSummaryIfNeeded() {
    // Always re-query to avoid stale references after DOM morph
    this.cartBookingLocation = document.querySelector('cart-booking-location');
    
    // Restore Step 3 state (cart summary with booking details)
    // @ts-ignore
    this.cartBookingLocation?.restoreBookingState();
    
    // Ensure booking summary visibility matches booking state
    this.updateBookingSummaryVisibility();
  }
  
  // #checkAndRemoveOrphanedServiceFee can be triggered twice by a single add:
  // CartAddEvent.eventName IS 'cart:update', so one dispatch reaches both open()
  // and the 500ms #handleCartUpdate timer. The second pass would double-POST the
  // same removal. Harmless but wasteful; collapse to one in-flight run.
  #feeCleanupInFlight = false;

  /**
   * Remove booking artifacts that no longer belong to the cart:
   * - the service fee line item, when the fee is switched off in theme settings or
   *   no government product remains. This is what makes turning the setting off
   *   safe: the fee is hidden from the cart item list and shown only in the
   *   footer, so a stale fee left behind would be charged with almost nothing to
   *   signal it.
   * - the booking-details cart attribute, when no government product remains. It
   *   carries the appointment id from saveBooking, and nothing else ever clears
   *   it, so with express checkout a wellness order could otherwise complete
   *   carrying the appointment id of a government service no longer in the cart.
   */
  async #checkAndRemoveOrphanedServiceFee() {
    if (this.#feeCleanupInFlight) return;
    this.#feeCleanupInFlight = true;
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Check if there are any government products
      const hasGovtProduct = cart.items.some(item => {
        // Check by product type metafield or by excluding service fee
        return item.handle !== 'salem-service-fee' && 
               item.product_type?.toLowerCase().includes('government');
      });
      
      // Also check DOM for government products (more reliable)
      const govtProductsInDOM = document.querySelectorAll('.cart-items__table-row[data-product-type="government"]');
      const hasGovtProductInDOM = govtProductsInDOM.length > 0;
      
      // Find service fee in cart
      const serviceFeeItem = cart.items.find(item => item.handle === 'salem-service-fee');
      
      // Remove the fee when it is switched off, or when it is orphaned by having no
      // government product left in the cart.
      const feeDisabled = !isServiceFeeEnabled();
      const isOrphaned = !hasGovtProduct && !hasGovtProductInDOM;

      if (serviceFeeItem && (feeDisabled || isOrphaned)) {
        
        // Get sections to update for proper cart refresh
        const cartComponent = this.querySelector('cart-items-component');
        const cartSectionId = cartComponent?.dataset?.sectionId;
        const sectionsParam = cartSectionId ? `&sections=${cartSectionId}` : '';
        
        const removeResponse = await fetch(`${Theme.routes.cart_change_url}?sections_url=${window.location.pathname}${sectionsParam}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: serviceFeeItem.key,
            quantity: 0
          })
        });
        
        const result = await removeResponse.json();
        
        // Hide the service fee display in footer immediately
        const serviceFeeDisplay = document.querySelector('.cart__service-fee');
        if (serviceFeeDisplay) {
          serviceFeeDisplay.style.display = 'none';
        }
        
        // Re-render the cart section to update totals and UI
        const cartItemsComponent = this.querySelector('cart-items-component');
        const sectionId = cartItemsComponent?.dataset?.sectionId;
        if (sectionId) {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        }
      }

      // With no government item left, any saved appointment id is stale. Clear it
      // so a later (express) checkout cannot attach a removed government service's
      // appointment to an unrelated order.
      if (isOrphaned && cart.attributes?.['booking-details']) {
        await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: { 'booking-details': '' }
          })
        });
      }
    } catch (e) {
      console.warn('Error checking for orphaned service fee:', e);
    } finally {
      this.#feeCleanupInFlight = false;
    }
  }
  
  /**
   * Show/hide checkout button based on booking step
   * Uses shared BookingStateManager for state access
   */
  #updateCheckoutButtonVisibility() {
    const state = BookingStateManager.getState();
    BookingStateManager.updateCheckoutButtonVisibility(state);
  }

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal(); 
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    if (window.innerWidth > 749) {
      setTimeout(() => {
        this.addEventListener('click', this.customHandleClick);
        this.addEventListener('keydown', this.customHandleKeyDown);
      });
    }
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.customHandleClick);
    this.removeEventListener('keydown', this.customHandleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

    toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialogCustom();
    } else {
      this.showDialog();
    }
  };

    
  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  customHandleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialogCustom();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  customHandleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialogCustom();
  }

  /**
   * Open the cart drawer and restore booking state
   */
  open() {
    this.showDialog();
    this.restoreBookingState();
    this.setupInstallmentsHandler();
    // Also check on open, not just on cart:update. The fee may have been switched off
    // since this cart was last touched, and a returning customer can reach checkout
    // from here without mutating the cart, which would otherwise leave them paying a
    // withdrawn fee that is only visible in the footer.
    this.#checkAndRemoveOrphanedServiceFee();
    // BookingButtonManager.updateState();
  }

  /**
   * Restore the booking state when drawer opens
   * This ensures the correct view is shown based on localStorage state
   */
  restoreBookingState() {
    // Always re-query to avoid stale references after DOM morph
    this.cartBookingJourney = document.querySelector('cart-booking-journey');
    this.cartBookingLocation = document.querySelector('cart-booking-location');

    // Normalize state on restore - if on Step 2 with Panel 1/2, reset to Panel 0
    // This ensures users see the main cart when reopening after exiting location selection
    // @ts-ignore
    this.cartBookingJourney?.normalizeStateOnRestore?.();

    // Restore Step 1 state (application flow)
    // @ts-ignore
    this.cartBookingJourney?.initState?.();
    // @ts-ignore
    this.cartBookingJourney?.manageBookingStates?.();
    // @ts-ignore
    this.cartBookingJourney?.loadProductFromLocalStorage?.();
    // @ts-ignore
    this.cartBookingJourney?.restoreCustomerData?.();

    // Restore Step 2 state (location/datetime flow)
    // @ts-ignore
    this.cartBookingLocation?.loadSelectedLocation?.();
    // @ts-ignore
    this.cartBookingLocation?.updateButtonState?.();

    // Restore Step 3 state (cart summary with booking details)
    // @ts-ignore
    this.cartBookingLocation?.restoreBookingState?.();

    // Ensure booking summary visibility matches booking state
    // If not confirmed, hide summary and show location button
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.updateBookingSummaryVisibility();
    });

    // Populate customer data for government products in cart
    this.populateCustomerData();

    // Update button visibility based on customer data state
    this.updateCustomerDataState();
    this.updateNoBookingRequiredState();

    // Update progress steps (handles wellness products with items in cart)
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => this.updateProgressSteps());
  }

  /**
   * Update progress step checkmarks based on cart state and booking state
   * Step 1 is complete if cart has items (wellness) or booking step > 1 (government)
   */
  updateProgressSteps() {
    // Get booking state
    let bookingState = null;
    try {
      const data = localStorage.getItem('booking_data');
      bookingState = data ? JSON.parse(data) : null;
    } catch (e) {
      bookingState = null;
    }

    const currentStep = bookingState?.step || 1;
    
    // Check if cart has items - use correct selector for cart item rows
    const cartItemRows = this.querySelectorAll('.cart-items__table-row[data-key]');
    const cartHasItems = cartItemRows.length > 0;

    // Find all progress step number elements
    const progressStepNumbers = this.querySelectorAll('[data-progress-step-number]');

    progressStepNumbers.forEach(stepEl => {
      const stepNumber = parseInt(stepEl.getAttribute('data-progress-step-number'), 10);
      const progressStep = stepEl.closest('.progress-step');

      // Step 1 (Add Services) is completed if:
      // - Cart has items (wellness flow adds items directly), AND
      // - Current step is beyond 1 (government flow)
      // If cart is empty, no checkmarks should show
      const isStep1Completed = stepNumber === 1 && cartHasItems && currentStep >= 1;
      const isStepCompleted = (stepNumber < currentStep && cartHasItems) || isStep1Completed;

      if (isStepCompleted) {
        // This step is completed - show checkmark
        if (!stepEl.querySelector('svg')) {
          stepEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17.9425 6.06705L7.94254 16.0671C7.88449 16.1252 7.81556 16.1713 7.73969 16.2027C7.66381 16.2342 7.58248 16.2504 7.50035 16.2504C7.41821 16.2504 7.33688 16.2342 7.26101 16.2027C7.18514 16.1713 7.11621 16.1252 7.05816 16.0671L2.68316 11.6921C2.56588 11.5748 2.5 11.4157 2.5 11.2499C2.5 11.084 2.56588 10.925 2.68316 10.8077C2.80044 10.6904 2.9595 10.6245 3.12535 10.6245C3.2912 10.6245 3.45026 10.6904 3.56753 10.8077L7.50035 14.7413L17.0582 5.18267C17.1754 5.0654 17.3345 4.99951 17.5003 4.99951C17.6662 4.99951 17.8253 5.0654 17.9425 5.18267C18.0598 5.29995 18.1257 5.45901 18.1257 5.62486C18.1257 5.79071 18.0598 5.94977 17.9425 6.06705Z" fill="currentColor"/>
          </svg>`;
        }
        progressStep?.classList.add('progress-step--completed');
        progressStep?.classList.remove('progress-step--active');
      } else if (stepNumber === currentStep || (stepNumber === 1 && currentStep === 1 && !cartHasItems)) {
        // This is the current step - show number, mark as active
        // if (stepEl.querySelector('svg')) {
        //   stepEl.textContent = String(stepNumber);
        // }
        progressStep?.classList.add('progress-step--active');
        progressStep?.classList.remove('progress-step--completed');
      } else {
        // Future step - show number, remove active/completed
        // if (stepEl.querySelector('svg')) {
        //   stepEl.textContent = String(stepNumber);
        // }
        progressStep?.classList.remove('progress-step--active', 'progress-step--completed');
      }
    });
  }

  /**
   * Populate customer data from localStorage for government products
   */
  populateCustomerData() {
    try {
      const customerDataStr = localStorage.getItem('customerData');
      if (!customerDataStr) return;

      const customerData = JSON.parse(customerDataStr);
      
      // Find all customer info containers in cart items
      const customerInfoContainers = document.querySelectorAll('[data-customer-info]');
      customerInfoContainers.forEach(container => {
        const nameEl = container.querySelector('[data-customer-name]');
        const appNumberEl = container.querySelector('[data-customer-app-number]');
        const appNumberRow = appNumberEl?.closest('p');
        
        if (nameEl && customerData.applicantName) {
          nameEl.textContent = customerData.applicantName;
        }
        if (appNumberEl && customerData.applicationNumber) {
          appNumberEl.textContent = customerData.applicationNumber;
        } else if (appNumberRow) {
          appNumberRow.style.display = 'none';
        }
      });
    } catch (e) {
      console.error('Error populating customer data:', e);
    }
  }


  /**
   * Update the data-needs-customer-data attribute based on localStorage
   * This controls whether checkout or finalise booking button is shown
   */
  updateCustomerDataState() {
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (!cartDrawerInner) {
      return;
    }

    const customerDataStr = localStorage.getItem('customerData');
    const needsCustomerData = !customerDataStr;
    cartDrawerInner.setAttribute('data-needs-customer-data', needsCustomerData.toString());
  }

  /**
   * Keep data-no-booking-required in sync with the cart contents.
   *
   * Liquid renders the attribute on .cart-drawer__inner, which is the PARENT of the
   * morphed cart-items-component and so is not re-rendered when the cart changes.
   * Without this, removing the last government item from a mixed cart would leave the
   * customer blocked behind a booking that no longer applies until a full page load,
   * and adding a government item to a wellness cart would leave checkout wrongly open.
   */
  updateNoBookingRequiredState() {
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (!cartDrawerInner) {
      return;
    }

    const noBookingRequired =
      isWellnessExpressCheckoutEnabled() &&
      BookingStateManager.hasItemsInCart() &&
      !BookingStateManager.hasGovtItemsInCart();

    cartDrawerInner.setAttribute('data-no-booking-required', noBookingRequired.toString());
  }

  /**
   * Setup handler for the Finalise Booking button
   * When clicked, opens the new customer form (Panel 2 with application type 'new')
   */
  setupFinaliseBookingHandler() {
    // Use event delegation since button may be re-rendered
    document.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const finaliseBtn = target?.closest('[data-finalise-booking-btn]');
      if (!finaliseBtn) return;

      console.log('[DEBUG Finalise Booking] clicked');
      event.preventDefault();
      this.openNewCustomerForm();
    });
  }

  /**
   * Open the new customer form for non-government (wellness) products
   * Sets up the booking journey to show Panel 2 with 'new' application type
   * Marks this as a wellness flow so previous/confirm behave differently
   */
  openNewCustomerForm() {
    // Always re-query to avoid stale references after DOM morph
    this.cartBookingJourney = document.querySelector('cart-booking-journey');
    console.log('[DEBUG openNewCustomerForm]', {
      found: !!this.cartBookingJourney,
      isConnected: this.cartBookingJourney?.isConnected,
      hasUpdateFn: typeof this.cartBookingJourney?.updateStep1ButtonState === 'function'
    });

    if (!this.cartBookingJourney) return;

    // Set the booking state to show new application form
    // step: 1 = APPLICATION step (matches BOOKING_STEPS.APPLICATION)
    // isWellnessFlow: true = item already in cart, skip add to cart on confirm
    //
    // serviceFeeApplies is carried over from the current state: a mixed cart can
    // reach Finalise Booking with an uncharged upgrade in it, and wiping the
    // explicit false would make shouldSendServiceFeeCode() re-attach 'SF'.
    // (undefined is dropped by JSON.stringify, keeping the reset clean otherwise.)
    let priorServiceFeeApplies;
    try {
      priorServiceFeeApplies = JSON.parse(localStorage.getItem('booking_data') || '{}').serviceFeeApplies;
    } catch (e) {
      priorServiceFeeApplies = undefined;
    }
    const bookingData = {
      step: 1,
      applicationType: 'new',
      panel: 2,
      isWellnessFlow: true,
      serviceFeeApplies: priorServiceFeeApplies
    };
    localStorage.setItem('booking_data', JSON.stringify(bookingData));

    // Update the cart drawer inner to show step 1 panel 2
    const cartDrawerInner = document.querySelector('.cart-drawer__inner');
    if (cartDrawerInner) {
      cartDrawerInner.setAttribute('data-step', '1');
      cartDrawerInner.setAttribute('data-panel', '2');
      cartDrawerInner.setAttribute('data-application-type', 'new');
    }

    // Update the booking journey component's internal state (guard against un-upgraded element)
    if (typeof this.cartBookingJourney.updateStep1ButtonState === 'function') {
      this.cartBookingJourney.selectedOption = 'new';
      this.cartBookingJourney.currentPanel = 2;
      this.cartBookingJourney.isWellnessFlow = true;
      this.cartBookingJourney.updateStep1ButtonState(2);
    }
  }

  /**
   * Setup handler for installments CTA to close drawer
   */
  setupInstallmentsHandler() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  // Flag to skip warning on next close
  _skipWarning = false;

  /**
   * Override closeDialog to intercept escape key and click-outside closes
   * This is called by the parent DialogComponent's event handlers
   */
  closeDialogCustom = async () => {
    // Check if on Step 3 (booking confirmed) and warning not already shown
    if (this.isOnStep3() && !this._skipWarning) {
      this.showCloseWarning();
      return;
    }
    this._skipWarning = false;
    // Directly close the dialog
    await this._doClose();
  };

  /**
   * Close the cart drawer - public method for external calls
   * Intercepts close on Step 3 to show warning
   */
  close() {
    this.closeDialogCustom();
  }

  /**
   * Force close the dialog without warning (used after Remove confirmation)
   */
  forceClose() {
    this._skipWarning = true;
    this._doClose();
  }

  /**
   * Actually close the dialog (internal method)
   */
  async _doClose() {
    // const state = BookingStateManager.getState();
    // if (!state || state.step === BOOKING_STEPS.APPLICATION) {
    //   // set to Panel 1
    //   // BookingStateManager.setState({ step: 1 });
    //   BookingStateManager.setState({ 
    //     step: BOOKING_STEPS.APPLICATION, 
    //     panel: 0 
    //   });
    // }
    this.closeDialog();
  }

  /**
   * Check if currently on Step 3 (Cart Summary with confirmed booking)
   * Uses shared BookingStateManager
   */
  isOnStep3() {
    return BookingStateManager.isBookingConfirmed();
  }

  /**
   * Show the close booking warning dialog
   */
  showCloseWarning() {
    const dialog = /** @type {HTMLDialogElement} */ (document.querySelector('[data-close-warning-overlay]'));
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  }

  /**
   * Hide the close booking warning dialog
   */
  hideCloseWarning() {
    const dialog = /** @type {HTMLDialogElement} */ (document.querySelector('[data-close-warning-overlay]'));
    if (dialog && typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  /**
   * Setup handlers for the close warning dialog buttons
   */
  setupCloseWarningHandlers() {
    const dialog = document.querySelector('[data-close-warning-overlay]');
    if (!dialog) return;

    const cancelBtn = dialog.querySelector('[data-close-warning-cancel]');
    const removeBtn = dialog.querySelector('[data-close-warning-remove]');

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.hideCloseWarning());
    }
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.handleRemoveBookingData());
    }
  }

  /**
   * Update booking summary visibility based on booking state
   * Hides summary and shows location button when booking is not confirmed
   */
  updateBookingSummaryVisibility() {
    let isConfirmed = false;
    try {
      const data = localStorage.getItem('booking_data');
      const state = data ? JSON.parse(data) : null;
      isConfirmed = state?.bookingConfirmed === true;
    } catch (e) {
      isConfirmed = false;
    }

    const bookingSummary = /** @type {HTMLElement | null} */ (document.querySelector('#booking-summary'));
    const locationBtn = /** @type {HTMLElement | null} */ (document.querySelector('booking-location-btn'));

    if (!isConfirmed) {
      // Hide booking summary, show location button
      if (bookingSummary) {
        bookingSummary.style.display = 'none';
      }
      if (locationBtn) {
        locationBtn.style.display = '';
      }
    }
  }

  /**
   * Handle the Remove action from the warning dialog
   * Clears location data, resets to Step 2, and closes the drawer
   */
  handleRemoveBookingData() {
    
    // Clear location-related data from booking_data
    try {
      const data = localStorage.getItem('booking_data');
      const state = data ? JSON.parse(data) : {};
      
      // Reset to Step 2 with Panel 0 (main cart view)
      // Clear all location/datetime related fields
      const newState = {
        ...state,
        step: 2,
        panel: 0,
        bookingConfirmed: false,
        locationId: null,
        locationName: null,
        locationImage: null,
        selectedDate: null,
        selectedTime: null,
        slotId: null,
        appointmentDate: null,
        appointmentTime: null,
        appointmentTimeText: null
      };

      localStorage.setItem('booking_data', JSON.stringify(newState));
    } catch (e) {
      console.error('Error clearing booking data:', e);
    }

    // Update DOM attributes directly
    const cartInner = /** @type {HTMLElement} */ (this.querySelector('.cart-drawer__inner'));
    if (cartInner) {
      cartInner.dataset.step = '2';
      cartInner.dataset.panel = '0';
      cartInner.classList.remove('booking-confirmed');
    }

    // Hide booking summary and reset location button
    const bookingSummary = /** @type {HTMLElement | null} */ (document.querySelector('#booking-summary'));
    const locationBtn = /** @type {HTMLElement | null} */ (document.querySelector('booking-location-btn'));
    
    if (bookingSummary) {
      bookingSummary.style.display = 'none';
    }
    if (locationBtn) {
      // Remove any inline style to let CSS take over based on data-step
      locationBtn.style.removeProperty('display');
    }

    // Hide the warning and force close the drawer (bypass Step 3 check)
    this.hideCloseWarning();
    this.forceClose();
  }

  /**
   * Setup handler for the Checkout button
   * Clears booking-related localStorage items when checkout is clicked
   */
  setupCheckoutHandler() {
    const checkoutBtn = this.querySelector('[data-checkout-btn]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        this.clearBookingLocalStorage();
      });
    }
  }

  /**
   * Clear all booking journey related localStorage items
   * Uses shared BookingStateManager
   */
  clearBookingLocalStorage() {
    BookingStateManager.clearAllBookingData();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
